<script setup lang="ts">
import type { StepEntity } from 'v-onboarding'
import type { Banner } from '~/components/shared/BannerCarousel/index.vue'
import { SolButton } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import IconPlus from '~icons/material-symbols/add'
import BannerCarousel from '~/components/shared/BannerCarousel/index.vue'
import RevalidateBanner from '~/components/shared/RevalidateBanner.vue'
import { useTrackCarouselBannerClick } from '~/composables/analytics/useTrackCarouselBannerClick'
import { useMyWalletAccess } from '~/features/my-wallet/composables/useMyWalletAccess'
import useAuth from '~/store-v2/auth/useAuth'
import { INCENTIVE_CAMPAIGN_TYPE_REVALIDATION, INCENTIVE_TARGET_TYPE_PARTNER } from '~/store-v2/incentives/incentivesQuery.constants'
import useTargetIncentivesSummary from '~/store-v2/incentives/useTargetIncentivesSummary'
import usePartner from '~/store-v2/partners/usePartner'
import { useImpersonateStore } from '~/store/impersonate'
import { useOnboardingStore } from '~/store/onboarding'
import NewsCarousel from '~/ui-blocks/overview/NewsCarousel/index.vue'
import PaymentModelInfo from '~/ui-blocks/overview/PaymentModelInfo/index.vue'
import ProjectsInProgress from '~/ui-blocks/overview/ProjectsInProgress/ProjectsInProgress.vue'
import WalletHealth from '~/ui-blocks/overview/WalletHealth/index.vue'

const router = useRouter()
const impersonateStore = useImpersonateStore()
const { t } = useI18n()
const onboardingStore = useOnboardingStore()
const { trackCarouselBannerClick } = useTrackCarouselBannerClick()
const { isEnabled: isMyWalletEnabled } = useMyWalletAccess()
const isReceivingModelHidden = ref(false)

const { data: auth } = useAuth()
const { checkIsImpersonate } = useImpersonate()
watch(() => auth.value?.me?.perfil, (perfil) => {
  if (perfil === 'admin') {
    checkIsImpersonate()
  }
}, { immediate: true })

const isAdminNotImpersonating = computed(() => {
  return auth.value?.me?.perfil === 'admin' && !impersonateStore.verifyIsImpersonated()
})

const basePartnerId = computed(() => {
  const perfil = auth.value?.me?.perfil
  if (perfil === 'admin' && impersonateStore.partnerId > 0) {
    return impersonateStore.partnerId.toString()
  }
  return auth.value?.person?.partner?.id?.toString() || ''
})
const { data: partner, isLoading: isLoadingPartner } = usePartner(basePartnerId)
const userIsOnBlockedList = computed(() => {
  return partner.value?.blocklist?.is_blocked || false
})

const isNewSimulationDisabled = computed(() => {
  return userIsOnBlockedList.value || isAdminNotImpersonating.value
})

const { screenWidth } = useScreenSize()

const buttonSize = computed(() => {
  return screenWidth.value <= 480 ? 'small' : 'large'
})

function newSimulation() {
  router.push(userIsOnBlockedList.value ? '/projects' : '/simulation/new')
}

const cdn = useCdn()

const disparaSolfacilBanner = `${cdn}financial-simulator/images/home-carousel/home_fintech_dispara_solfacil.png`
const boletoEmDiaBanner = `${cdn}financial-simulator/images/home-carousel/boleto-em-dia.png`

const { data: incentiveData, isError: isIncentiveError } = useTargetIncentivesSummary({
  targetId: basePartnerId,
  targetType: INCENTIVE_TARGET_TYPE_PARTNER,
  campaignType: INCENTIVE_CAMPAIGN_TYPE_REVALIDATION,
})

const revalidationBannerData = computed(() => {
  if (!incentiveData.value)
    return null

  return {
    image: incentiveData.value.banner_url,
    imageAlt: 'Pessoa revalidando instalações pelo celular',
    title: incentiveData.value.title_ptbr,
    description: incentiveData.value.description_ptbr,
    ctaLabel: 'Comece agora',
  }
})

const hasRevalidationBanner = computed(() =>
  !isIncentiveError.value && !!revalidationBannerData.value,
)

const REVALIDATION_BANNER_URL = '/projects?provider=revalidation'

function goToRevalidations() {
  router.push({ path: '/projects', query: { provider: 'revalidation' } })
}

const banners = computed<Banner[]>(() => {
  const list: Banner[] = [
    {
      name: 'dispara_solfacil',
      imageXs: disparaSolfacilBanner,
      imageSm: disparaSolfacilBanner,
      imageMd: disparaSolfacilBanner,
      imageLg: disparaSolfacilBanner,
      link: 'https://premiacao.solfacil.com.br/sorteio?utm_source=financiamento_solfacil&utm_medium=banner&utm_campaign=dispara_solfacil&utm_content=banner_carrossel_home',
      alt: t('overview.banner.dispara_solfacil_alt'),
    },
    {
      name: 'boleto_em_dia',
      imageXs: boletoEmDiaBanner,
      imageSm: boletoEmDiaBanner,
      imageMd: boletoEmDiaBanner,
      imageLg: boletoEmDiaBanner,
      link: 'https://boletoemdia.solfacil.com.br/?utm_source=financiamento_solfacil&utm_medium=banner&utm_campaign=boleto_em_dia_sorte_na_mao&utm_content=banner_carro_com_solar',
      alt: t('overview.banner.boleto_em_dia_alt'),
    },
  ]

  if (hasRevalidationBanner.value && revalidationBannerData.value) {
    list.push({
      name: 'revalidation',
      url: REVALIDATION_BANNER_URL,
      component: markRaw(RevalidateBanner),
      componentProps: {
        ...revalidationBannerData.value,
        onCtaClick: goToRevalidations,
      },
    })
  }

  return list
})

const news = computed(() => [
  {
    id: 1,
    image: '/images/news/news-combo-conecte.svg',
    title: t('overview.news.combo_conecte.title'),
    description: `
      ${t('overview.news.combo_conecte.description.prefix')} <strong>${t('overview.news.combo_conecte.description.highlight')}</strong>
      ${t('overview.news.combo_conecte.description.suffix')}
    `,
    buttonText: t('overview.news.combo_conecte.button_text'),
    action: () => {
      router.push('/simulation/new')
    },
  },
  {
    id: 2,
    image: '/images/news/news-combo-facil.svg',
    title: t('overview.news.combo_facil.title'),
    description: `
      ${t('overview.news.combo_facil.description.prefix')} <strong>${t('overview.news.combo_facil.description.highlight')}</strong>
      ${t('overview.news.combo_facil.description.suffix')}
    `,
    buttonText: t('overview.news.combo_facil.button_text'),
    action: () => {
      window.open('https://solfacil.com.br/combo-facil/', '_blank')
    },
  },
  {
    id: 3,
    image: '/images/news/news-seguros.svg',
    title: t('overview.news.seguros.title'),
    description: `
      ${t('overview.news.seguros.description.prefix')}
      <strong>${t('overview.news.seguros.description.highlight')}</strong>
      ${t('overview.news.seguros.description.middle')}
      <strong>${t('overview.news.seguros.description.highlight2')}</strong>.
      ${t('overview.news.seguros.description.suffix')}
    `,
    buttonText: t('overview.news.seguros.button_text'),
    action: () => {
      window.open('https://solfacil.com.br/seguros', '_blank')
    },
  },
])

const onboardingSteps = ([
  {
    attachTo: { element: '#new-simulation-anchor' },
    content: {
      title: t('onboarding.pages.overview.step1.title'),
      description: t('onboarding.pages.overview.step1.description'),
    },
  },
  {
    attachTo: { element: '#projects-in-progress' },
    content: {
      title: t('onboarding.pages.overview.step2.title'),
      description: t('onboarding.pages.overview.step2.description'),
    },
    options: {
      scrollToStep: {
        enabled: true,
        options: { block: 'center', inline: 'nearest', behavior: 'auto' } as const,
      },
    },
  },
  {
    attachTo: { element: '.overview-row-carousels' },
    content: {
      title: t('onboarding.pages.overview.step3.title'),
      description: t('onboarding.pages.overview.step3.description'),
    },
  },
  {
    attachTo: { element: '#wallet-health-anchor' },
    content: {
      title: t('onboarding.pages.overview.step4.title'),
      description: t('onboarding.pages.overview.step4.description'),
    },
  },
  {
    attachTo: { element: '#payment-model-anchor' },
    content: {
      title: t('onboarding.pages.overview.step5.title'),
      description: t('onboarding.pages.overview.step5.description'),
    },
  },
] satisfies StepEntity[])

const OVERVIEW_TOUR_ID = 'page-overview'

onMounted(() => {
  onboardingStore.registerTour({
    id: OVERVIEW_TOUR_ID,
    steps: onboardingSteps,
    priority: 10,
    storageKey: 'onboarding:page-overview:completed',
  })
})

onBeforeUnmount(() => {
  onboardingStore.unregisterTour(OVERVIEW_TOUR_ID)
})
</script>

<template>
  <div class="overview-page">
    <div class="overview-container">
      <div class="page-heading">
        <h2 class="fonts-heading-h2">
          {{ t('overview.title') }}
        </h2>
        <div
          v-if="auth?.person.partner?.permission.financing"
          id="new-simulation-anchor"
          data-onboarding="new-simulation"
          class="inline-flex"
        >
          <SolButton
            id="new-simulation"
            :disabled="isNewSimulationDisabled"
            :loading="isLoadingPartner"
            :size="buttonSize"
            @click="newSimulation"
          >
            <template #icon-left>
              <IconPlus />
            </template>
            {{ t('overview.projects_in_progress.empty.new_simulation') }}
          </SolButton>
        </div>
      </div>
      <div class="overview-content">
        <div class="overview-row">
          <div id="projects-in-progress">
            <ProjectsInProgress />
          </div>
        </div>
        <div class="overview-row overview-row-carousels">
          <BannerCarousel :banners="banners" @banner-click="trackCarouselBannerClick" />
          <NewsCarousel :news="news" />
        </div>
        <div class="overview-row">
          <div id="wallet-health-anchor" class="overview-equal-height-card">
            <WalletHealth />
          </div>
          <div v-if="isMyWalletEnabled" v-show="!isReceivingModelHidden" id="payment-model-anchor" class="overview-equal-height-card">
            <PaymentModelInfo v-model:hidden="isReceivingModelHidden" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.overview-page {
  @apply flex flex-col min-h-full py-2xs px-4xs;

  @screen md:system {
    @apply px-2xs pb-sm pt-2xs;
  }

  .overview-container {
    @apply flex flex-col gap-6;

    .page-heading {
      @apply flex items-center justify-between gap-4;

      h2 {
        @apply fonts-heading-h2 text-neutral-low-pure;
      }
    }

    .overview-content {
      @apply flex flex-col gap-6;

      .overview-equal-height-card {
        @apply flex flex-col self-stretch;

        :deep(.card-session) {
          @apply flex flex-col flex-1 w-full;
        }

        :deep(.card-session-content) {
          @apply flex-1;
        }
      }

      .overview-row {
        @apply flex flex-col gap-6;

        @screen md:system {
          @apply flex-row items-stretch gap-6;

          > * {
            @apply flex-1 min-h-0;
          }
        }

        &.overview-row-carousels {
          @apply flex-col;

          > * {
            @apply w-full;
          }

          @screen sm:system {
            @apply flex-row items-stretch gap-6;

            > :first-child {
              @apply flex-none;
              @apply w-[309px];
            }

            > :last-child {
              @apply flex-1;
              @apply min-w-0;
            }
          }

          @screen md:system {
            @apply gap-6 ;

            > :first-child {
              @apply flex-none;
              @apply w-[309px];
            }

            > :last-child {
              @apply flex-1;
              @apply min-w-0;
            }
          }

          @screen lg:system {
            @apply gap-6 ;

            > :first-child {
              @apply flex-none;
              @apply w-[424px];
            }
          }
        }
      }
    }
  }
}
</style>
